import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, PenSquare, Image as ImageIcon, FileText, Download } from "lucide-react";
import { productImgSrc } from "@/pages/Products";

const bytesLabel = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Full product view — image, description, files list. SuperAdmin gets inline
 * edit / delete / upload; members are read-only but can download every file.
 */
export default function ProductDetail({ product, open, onOpenChange, isAdmin, onEdit, onDeleted, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  if (!product) return null;

  const refresh = async () => {
    try {
      const { data } = await api.get(`/products/${product.id}`);
      onChanged?.(data);
    } catch { /* ignore */ }
  };

  const download = async (f) => {
    try {
      // f.url is like "/api/uploads/products/xxx.pdf" — api instance already has baseURL /api,
      // strip the prefix so axios prepends REACT_APP_BACKEND_URL correctly.
      const relative = f.url.replace(/^\/api/, "");
      const { data } = await api.get(relative, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: f.mime || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = f.name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/products/${product.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File added");
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileInput.current) fileInput.current.value = ""; }
  };

  const removeFile = async (fid) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await api.delete(`/products/${product.id}/files/${fid}`);
      toast.success("File removed");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteProduct = async () => {
    if (!window.confirm(`Delete "${product.name}"? This also removes every file.`)) return;
    setBusy(true);
    try {
      await api.delete(`/products/${product.id}`);
      toast.success("Product deleted");
      onDeleted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-detail-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-lg font-semibold text-slate-900">{product.name}</DialogTitle>
              {product.category && (
                <span className="inline-flex mt-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid="product-detail-category">
                  {product.category}
                </span>
              )}
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => onEdit?.(product)} data-testid="edit-product-btn">
                  <PenSquare className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={deleteProduct} disabled={busy} className="text-rose-600 hover:text-rose-700" data-testid="delete-product-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="aspect-[4/3] rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center">
            {product.image_url ? (
              <img src={productImgSrc(product.image_url)} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="w-10 h-10 text-slate-300" />
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Description</p>
            <p className="text-sm whitespace-pre-wrap leading-relaxed text-slate-700 min-h-[80px]">
              {product.description || <span className="italic text-slate-400">No description yet.</span>}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <p className="text-xs uppercase tracking-widest text-slate-500">Files</p>
            {isAdmin && (
              <>
                <Button size="sm" variant="ghost" onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="product-add-file-btn">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? "Uploading…" : "Add file"}
                </Button>
                <input ref={fileInput} type="file" className="hidden" onChange={uploadFile} data-testid="product-file-input" />
              </>
            )}
          </div>

          {(!product.files || product.files.length === 0) ? (
            <p className="text-xs text-slate-400 italic">No files uploaded yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {product.files.map((f) => (
                <li key={f.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid={`product-file-${f.id}`}>
                  <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{f.name}</p>
                    <p className="text-[11px] text-slate-500">{bytesLabel(f.size)} · added by {f.uploaded_by_name}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => download(f)} data-testid={`product-file-dl-${f.id}`}>
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" onClick={() => removeFile(f.id)} className="text-rose-600 hover:text-rose-700" data-testid={`product-file-del-${f.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
